<template>
  <div>
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="3">
            <FormulateInput
              id="name"
              v-model="filters.name"
              :label="$t('Nome')"
              :placeholder="$t('Nome')"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="type"
              v-model="filters.type"
              type="vue-select"
              :label="$t('Tipo')"
              :placeholder="$t('Selecione')"
              :options="calendarTypes"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="province"
              v-model="filters.province"
              type="vue-select"
              :label="$t('Estado')"
              :placeholder="$t('Selecione')"
              :options="provinceOptions"
              @input="onProvinceChange"
            />
          </b-col>
          <b-col md="3">
            <FormulateInput
              id="city"
              v-model="filters.city"
              type="vue-select"
              :label="$t('Cidade')"
              :placeholder="$t('Selecione')"
              :options="cityOptions"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="event-date"
              v-model="filters.eventDate"
              type="datepicker"
              :label="$t('Data')"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="calendar-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="$t('Nenhum registro encontrado')"
        :fields="fields"
        :items="seasonalCalendars"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(action)="row">
          <e-grid-actions
            :show-update="true"
            :show-delete="true"
            @update="showUpdateSidebar(row)"
            @delete="deleteCalendar(row)"
          />
        </template>
      </b-table>
      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="calendar-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <CalendarSidebar
      ref="sidebar"
      @save="getData"
    />

    <fab
      :main-tooltip="$t('Adicionar')"
      @click="showCreateSidebar"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import { province } from '@/mixins'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { EPageSizeSelector, EPaginationSummary } from '@/views/components'
import fab from '@/views/components/FAB.vue'
import EFilters from '@/views/components/EFilters.vue'
import EGridActions from '@/views/components/EGridActions.vue'
import CalendarSidebar from './CalendarSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    EFilters,
    EGridActions,
    CalendarSidebar,
  },
    
    mixins: [province],
    
  data() {
    return {
      fetching: false,
    }
  },
  computed: {
    ...mapGetters('app', [
      'activeStoreOptions',
    ]),
    ...mapGetters('pages/settings/calendar', [
      'calendarTypes',
      'cities',
    ]),
    ...mapState('pages/settings/calendar', [
      'seasonalCalendars',
      'paging',
      'sorting',
      'filters',
    ]),
    fields() {
      return [
        { label: this.$t('Actions'), key: 'action', class: 'text-center', thStyle: { width: '100px' } },
        { label: this.$t('ID'), key: 'id', class: 'text-center', thStyle: { width: '100px' }, sortable: true },
        { label: this.$t('Nome'), key: 'name', sortable: true },
        { label: this.$t('Tipo'), key: 'type', class: 'text-center', thStyle: { width: '120px' }, sortable: true,
          formatter: val => this.typeLabel[val] || '-', },
        { label: this.$t('Data de Início'), key: 'startDate', sortable: true, 
          formatter: val => this.$options.filters.date(val, '-') },
        { label: this.$t('Data de Término'), key: 'endDate', sortable: true, 
          formatter: val => this.$options.filters.date(val, '-') },
        { label: this.$t('Descrição'), key: 'description' },
        { label: this.$t('Fator de Compra'), key: 'purchaseFactor',
          formatter: val => this.$options.filters.number(val, 2) },
      ]
    },
    typeLabel() {
      return {
        NationalHoliday: this.$t('Feriado Nacional'),
        StateHoliday: this.$t('Feriado Estadual'),
        MunicipalHoliday: this.$t('Feriado Municipal'),
        Event: this.$t('Evento'),
        Other: this.$t('Outro')
      }
    },
    provinceOptions() {
      return (this.provincesWithStores() || []).map(p => ({
        value: p.ibge,
        label: p.value,
      }))
    },
    cityOptions() {
      return this.cities.map(city => ({
        value: city.idIbge,
        label: city.name,
      }))
    },
  },
  mounted() {
    this.getData()
  },
  methods: {
    ...mapActions('pages/settings/calendar', [
      'fetchSeasonalCalendars',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
      'setCities',
      'removeCalendar',
    ]),
    getData() {
      this.fetching = true
      this.fetchSeasonalCalendars().finally(() => {
        this.fetching = false
      })
    },
    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },
    filterData() {
      this.getData()
    },
    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },
    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },
    showCreateSidebar() {
      this.$refs.sidebar.show()
    },
    showUpdateSidebar(row) {
      this.$refs.sidebar.show({ id: row.item.id })
    },
    onProvinceChange(provinceId) {
      this.filters.city = null
      if (!provinceId) {
          this.setCities([])
          return
      }
      const cities = []
      this.activeStoreOptions.filter(store => store.address?.provinceCode.toString() === provinceId.toString()).forEach(store => {
          if (store.address?.cityCode && !cities.some(city => city.idIbge.toString() === store.address.cityCode.toString())) {
              cities.push({ idIbge: store.address.cityCode, name: store.address.city })
          }
      })
      this.setCities(cities)
    },
    async deleteCalendar(row) {
      try {
        const confirm = await this.confirm()
        if (confirm) {
          const id = row.item.id || row.item.localId
          this.removeCalendar(id)
        }
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style></style>
